import React, { useEffect, useState } from "react";
import MasterLayout from "../masterLayout/MasterLayout";
import Breadcrumb from "../components/Breadcrumb";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import api from "../utils/requests/base";
import { useAtom } from "jotai";
import { settingsAtom, usersAtom } from "../utils/atoms";

const ReportsPage = () => {
    // Settings for default email
    const [settings] = useAtom(settingsAtom);
    const defaultEmail = settings?.user?.email || "";

    // State for Maam report
    const [maamStartDate, setMaamStartDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
    const [maamEndDate, setMaamEndDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0));
    const [maamDelivery, setMaamDelivery] = useState("download");
    const [maamEmail, setMaamEmail] = useState(defaultEmail);
    const [maamLoading, setMaamLoading] = useState(false);
    const [maamSuccess, setMaamSuccess] = useState("");
    const [maamError, setMaamError] = useState("");
    const [maamDownloadUrl, setMaamDownloadUrl] = useState(null);

    // State for Income report
    const [incomeStartDate, setIncomeStartDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
    const [incomeEndDate, setIncomeEndDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0));
    const [incomeEmail, setIncomeEmail] = useState(defaultEmail);
    const [incomeLoading, setIncomeLoading] = useState(false);
    const [incomeSuccess, setIncomeSuccess] = useState("");
    const [incomeError, setIncomeError] = useState("");

    // State for Expense report
    const [expenseStartDate, setExpenseStartDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
    const [expenseEndDate, setExpenseEndDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0));
    const [expenseEmail, setExpenseEmail] = useState(defaultEmail);
    const [expenseLoading, setExpenseLoading] = useState(false);
    const [expenseSuccess, setExpenseSuccess] = useState("");
    const [expenseError, setExpenseError] = useState("");

    // State for Transactions report
    const [transactionsStartDate, setTransactionsStartDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
    const [transactionsEndDate, setTransactionsEndDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0));
    const [transactionsEmail, setTransactionsEmail] = useState(defaultEmail);
    const [transactionsStudentUID, setTransactionsStudentUID] = useState("");
    const [transactionsIgnoreCredit, setTransactionsIgnoreCredit] = useState(false);
    const [transactionsLoading, setTransactionsLoading] = useState(false);
    const [transactionsSuccess, setTransactionsSuccess] = useState("");
    const [transactionsError, setTransactionsError] = useState("");
    const [transactionsDelivery, setTransactionsDelivery] = useState("download");
    const [transactionsDownloadUrl, setTransactionsDownloadUrl] = useState(null);

    // State for Events report
    const [eventsStartDate, setEventsStartDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
    const [eventsEndDate, setEventsEndDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0));
    const [eventsEmail, setEventsEmail] = useState(defaultEmail);
    const [eventsStudentUID, setEventsStudentUID] = useState("");
    const [eventsTypes, setEventsTypes] = useState(['lesson', 'test', 'internalTest']); // array of selected event types
    const [eventsLoading, setEventsLoading] = useState(false);
    const [eventsSuccess, setEventsSuccess] = useState("");
    const [eventsError, setEventsError] = useState("");
    const [showEventsStudentModal, setShowEventsStudentModal] = useState(false);
    const [eventsStudentSearch, setEventsStudentSearch] = useState("");
    const [eventsStudentStatus, setEventsStudentStatus] = useState('active');
    const [eventsDelivery, setEventsDelivery] = useState("download");
    const [eventsDownloadUrl, setEventsDownloadUrl] = useState(null);

    // State for Students report
    const [studentsDelivery, setStudentsDelivery] = useState("download");
    const [studentsEmail, setStudentsEmail] = useState(defaultEmail);
    const [studentsLoading, setStudentsLoading] = useState(false);
    const [studentsSuccess, setStudentsSuccess] = useState("");
    const [studentsError, setStudentsError] = useState("");
    const [studentsDownloadUrl, setStudentsDownloadUrl] = useState(null);

    // Accordion state
    const [openAccordion, setOpenAccordion] = useState("");

    // Quick date range logic
    const setQuickDateRange = (type, setStart, setEnd) => {
        const today = new Date();
        let newStartDate, newEndDate;
        switch (type) {
            case 'currentMonth':
                newStartDate = new Date(today.getFullYear(), today.getMonth(), 1);
                newEndDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                break;
            case 'lastMonth':
                newStartDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                newEndDate = new Date(today.getFullYear(), today.getMonth(), 0);
                break;
            case 'currentYear':
                newStartDate = new Date(today.getFullYear(), 0, 1);
                newEndDate = new Date(today.getFullYear(), 11, 31);
                break;
            case 'prevBiMonth': {
                const month = today.getMonth();
                const year = today.getFullYear();
                let periodStartMonth, periodEndMonth, periodYear = year;
                if (month < 2) {
                    periodStartMonth = 10;
                    periodEndMonth = 11;
                    periodYear = year - 1;
                } else if (month < 4) {
                    periodStartMonth = 0;
                    periodEndMonth = 1;
                } else if (month < 6) {
                    periodStartMonth = 2;
                    periodEndMonth = 3;
                } else if (month < 8) {
                    periodStartMonth = 4;
                    periodEndMonth = 5;
                } else if (month < 10) {
                    periodStartMonth = 6;
                    periodEndMonth = 7;
                } else {
                    periodStartMonth = 8;
                    periodEndMonth = 9;
                }
                newStartDate = new Date(periodYear, periodStartMonth, 1);
                newEndDate = new Date(periodYear, periodEndMonth + 1, 0);
                break;
            }
            default:
                return;
        }
        setStart(newStartDate);
        setEnd(newEndDate);
    };

    // Handlers for Maam report
    const handleMaamSubmit = async (e) => {
        e.preventDefault();
        setMaamLoading(true);
        setMaamSuccess("");
        setMaamError("");
        setMaamDownloadUrl(null);
        try {
            if (maamDelivery === "download") {
                const res = await api.post(`/teacher/invoices/reports/maam`, {
                    startDate: maamStartDate.getTime(),
                    endDate: maamEndDate.getTime(),
                });
                if (res.data && res.data.documentUrl) {
                    setMaamDownloadUrl(res.data.documentUrl);
                    setMaamSuccess("הדו" + String.fromCharCode(8221) + "ח שלך מוכן להורדה!");
                } else {
                    setMaamError("לא התקבל קובץ להורדה");
                }
            } else {
                await api.post(`/teacher/invoices/reports/maam`, {
                    startDate: maamStartDate.getTime(),
                    endDate: maamEndDate.getTime(),
                    email: maamEmail,
                });
                setMaamSuccess("הדו" + String.fromCharCode(8221) + "ח נשלח לכתובת " + maamEmail);
            }
        } catch (err) {
            setMaamError("אירעה שגיאה בשליחת הדו" + String.fromCharCode(8221) + "ח.");
        } finally {
            setMaamLoading(false);
        }
    };

    // Handlers for Income report
    const handleIncomeSubmit = async (e) => {
        e.preventDefault();
        setIncomeLoading(true);
        setIncomeSuccess("");
        setIncomeError("");
        try {
            await api.post(`/teacher/invoices/reports/income`, {
                startDate: incomeStartDate.getTime(),
                endDate: incomeEndDate.getTime(),
                email: incomeEmail,
            });
            setIncomeSuccess("הדו" + String.fromCharCode(8221) + "ח נשלח לכתובת " + incomeEmail);
        } catch (err) {
            setIncomeError("אירעה שגיאה בשליחת הדו" + String.fromCharCode(8221) + "ח.");
        } finally {
            setIncomeLoading(false);
        }
    };

    // Handlers for Expense report
    const handleExpenseSubmit = async (e) => {
        e.preventDefault();
        setExpenseLoading(true);
        setExpenseSuccess("");
        setExpenseError("");
        try {
            await api.post(`/teacher/invoices/reports/expenses`, {
                startDate: expenseStartDate.getTime(),
                endDate: expenseEndDate.getTime(),
                email: expenseEmail,
            });
            setExpenseSuccess("הדו" + String.fromCharCode(8221) + "ח נשלח לכתובת " + expenseEmail);
        } catch (err) {
            setExpenseError("אירעה שגיאה בשליחת הדו" + String.fromCharCode(8221) + "ח.");
        } finally {
            setExpenseLoading(false);
        }
    };

    const handleTransactionsSubmit = async (e) => {
        e.preventDefault();
        setTransactionsLoading(true);
        setTransactionsSuccess("");
        setTransactionsError("");
        setTransactionsDownloadUrl && setTransactionsDownloadUrl(null);
        try {
            const body = {
                startDate: transactionsStartDate.getTime(),
                endDate: transactionsEndDate.getTime(),
            };
            if (transactionsStudentUID) body.studentUID = Number(transactionsStudentUID);
            if (transactionsIgnoreCredit) body.ignoreCredit = true;
            if (transactionsDelivery === "email") body.email = transactionsEmail;
            if (transactionsDelivery === "download") {
                const res = await api.post(`/teacher/invoices/reports/transactions`, body);
                if (res.data && res.data.documentUrl) {
                    setTransactionsDownloadUrl(res.data.documentUrl);
                    setTransactionsSuccess("הדו" + String.fromCharCode(8221) + "ח שלך מוכן להורדה!");
                } else {
                    setTransactionsError("לא התקבל קובץ להורדה");
                }
            } else {
                await api.post(`/teacher/invoices/reports/transactions`, body);
                setTransactionsSuccess("הדו" + String.fromCharCode(8221) + "ח נשלח לכתובת " + transactionsEmail);
            }
        } catch (err) {
            setTransactionsError("אירעה שגיאה בשליחת הדו" + String.fromCharCode(8221) + "ח.");
        } finally {
            setTransactionsLoading(false);
        }
    };

    const handleDownload = (url) => {
        if (url) {
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'maam-report.pdf');
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        }
    };

    const [users, setUsers] = useAtom(usersAtom);

    // Student picker modal state
    const [showStudentModal, setShowStudentModal] = useState(false);
    const [studentSearch, setStudentSearch] = useState("");
    const [studentStatus, setStudentStatus] = useState('active'); // 'active' or 'inactive'

    // Fetch students when opening the modal
    const handleOpenStudentModal = async () => {
        setShowStudentModal(true);
        try {
            const response = await api.get('/teacher/students');
            setUsers(response.data);
        } catch (err) {
            // Optionally handle error
        }
    };

    // Event types for the new report
    const EVENT_TYPES = [
        { value: "lesson", label: "שיעורים" },
        { value: "test", label: "טסטים" },
        { value: "internalTest", label: "טסטים פנימיים" },
        { value: "break", label: "הפסקות" },
        { value: "vacation", label: "חופשות" },
        { value: "lessonRequest", label: "בקשות שיעור" },
        { value: "cancelationRequest", label: "בקשות ביטול" },
    ];

    const handleEventsSubmit = async (e) => {
        e.preventDefault();
        setEventsLoading(true);
        setEventsSuccess("");
        setEventsError("");
        setEventsDownloadUrl(null);
        try {
            const body = {
                startDate: eventsStartDate.getTime(),
                endDate: eventsEndDate.getTime(),
            };
            if (eventsTypes.length > 0) body.types = eventsTypes;
            if (eventsStudentUID) body.studentUID = Number(eventsStudentUID);
            if (eventsDelivery === "email") body.email = eventsEmail;
            if (eventsDelivery === "download") {
                const res = await api.post(`/teacher/invoices/reports/events`, body);
                if (res.data && res.data.documentUrl) {
                    setEventsDownloadUrl(res.data.documentUrl);
                    setEventsSuccess("הדו" + String.fromCharCode(8221) + "ח שלך מוכן להורדה!");
                } else {
                    setEventsError("לא התקבל קובץ להורדה");
                }
            } else {
                await api.post(`/teacher/invoices/reports/events`, body);
                setEventsSuccess("הדו" + String.fromCharCode(8221) + "ח נשלח לכתובת " + eventsEmail);
            }
        } catch (err) {
            setEventsError("אירעה שגיאה בשליחת הדו" + String.fromCharCode(8221) + "ח.");
        } finally {
            setEventsLoading(false);
        }
    };

    const handleOpenEventsStudentModal = async () => {
        setShowEventsStudentModal(true);
        try {
            const response = await api.get('/teacher/students');
            setUsers(response.data);
        } catch (err) { }
    };

    const handleStudentsSubmit = async (e) => {
        e.preventDefault();
        setStudentsLoading(true);
        setStudentsSuccess("");
        setStudentsError("");
        setStudentsDownloadUrl(null);
        try {
            const body = {};
            if (studentsDelivery === "email") body.email = studentsEmail;
            if (studentsDelivery === "download") {
                const res = await api.post(`/teacher/invoices/reports/students`, body);
                if (res.data && res.data.documentUrl) {
                    setStudentsDownloadUrl(res.data.documentUrl);
                    setStudentsSuccess("הדו" + String.fromCharCode(8221) + "ח שלך מוכן להורדה!");
                } else {
                    setStudentsError("לא התקבל קובץ להורדה");
                }
            } else {
                await api.post(`/teacher/invoices/reports/students`, body);
                setStudentsSuccess("הדו" + String.fromCharCode(8221) + "ח נשלח לכתובת " + studentsEmail);
            }
        } catch (err) {
            setStudentsError("אירעה שגיאה בשליחת הדו" + String.fromCharCode(8221) + "ח.");
        } finally {
            setStudentsLoading(false);
        }
    };

    const selectedEventsStudent = users.find(s => String(s.uid) === String(eventsStudentUID));

    useEffect(() => {
        setQuickDateRange('prevBiMonth', setMaamStartDate, setMaamEndDate)
        setQuickDateRange('prevBiMonth', setIncomeStartDate, setIncomeEndDate)
        setQuickDateRange('prevBiMonth', setExpenseStartDate, setExpenseEndDate)
        setQuickDateRange('prevBiMonth', setTransactionsStartDate, setTransactionsEndDate)
    }, [])

    const selectedStudent = users.find(s => String(s.uid) === String(transactionsStudentUID))

    return (
        <>
            <MasterLayout>
                <Breadcrumb title={'Settings - דו"חות'} />
                <div className="container py-4">
                    <h2 className="mb-20">דו"חות</h2>
                    <div className="accordion" id="reportsAccordion">
                        {/* Group: דוחות כספיים */}
                        <div className="mb-4">
                            <h4 className="mb-3 text-xl opacity-75">דוחות כספיים</h4>
                            {/* דו"ח מע"מ */}
                            <div className="accordion-item">
                                <h2 className="accordion-header" id="headingMaam">
                                    <button
                                        className={`accordion-button${openAccordion === "maam" ? "" : " collapsed"}`}
                                        type="button"
                                        onClick={() => setOpenAccordion(openAccordion === "maam" ? null : "maam")}
                                        aria-expanded={openAccordion === "maam"}
                                        aria-controls="collapseMaam"
                                    >
                                        דו"ח מע"מ
                                    </button>
                                </h2>
                                <div
                                    id="collapseMaam"
                                    className={`accordion-collapse collapse${openAccordion === "maam" ? " show" : ""}`}
                                    aria-labelledby="headingMaam"
                                    data-bs-parent="#reportsAccordion"
                                >
                                    <div className="accordion-body">
                                        <form onSubmit={handleMaamSubmit}>
                                            <span className="text-sm fw-medium">טווח תאריכים:</span>
                                            <div className="d-flex align-items-start align-items-sm-center gap-2 flex-wrap mt-2 mb-3 flex-column flex-sm-row">
                                                <div className="d-flex gap-1 gap-sm-2 flex-column flex-sm-row align-items-start align-items-sm-center">
                                                    <div className="position-relative">
                                                        <DatePicker
                                                            selected={maamStartDate}
                                                            onChange={date => {
                                                                setMaamStartDate(date);
                                                                if (date && maamEndDate && date > maamEndDate) {
                                                                    setMaamEndDate(date);
                                                                }
                                                            }}
                                                            selectsStart
                                                            startDate={maamStartDate}
                                                            endDate={maamEndDate}
                                                            className="form-control form-control-sm"
                                                            dateFormat="dd/MM/yyyy"
                                                            placeholderText="תאריך התחלה"
                                                            locale="he"
                                                            minDate={new Date(2020, 0, 1)}
                                                            maxDate={maamEndDate || new Date(2030, 11, 31)}
                                                        />

                                                    </div>
                                                    <span>עד</span>
                                                    <div className="position-relative">
                                                        <DatePicker
                                                            selected={maamEndDate}
                                                            onChange={date => {
                                                                setMaamEndDate(date);
                                                                if (date && maamStartDate && date < maamStartDate) {
                                                                    setMaamStartDate(date);
                                                                }
                                                            }}
                                                            selectsEnd
                                                            startDate={maamStartDate}
                                                            endDate={maamEndDate}
                                                            className="form-control form-control-sm"
                                                            dateFormat="dd/MM/yyyy"
                                                            placeholderText="תאריך סיום"
                                                            locale="he"
                                                            minDate={maamStartDate || new Date(2020, 0, 1)}
                                                            maxDate={new Date(2030, 11, 31)}
                                                        />

                                                    </div>
                                                </div>
                                                <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setQuickDateRange('prevBiMonth', setMaamStartDate, setMaamEndDate)}>איפוס</button>
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label">אופן קבלת הדו"ח:</label>
                                                <div className="d-flex gap-0 gap-sm-5 align-items-start align-items-sm-center flex-column flex-sm-row">
                                                    <div className="form-check d-flex align-items-center">
                                                        <input
                                                            className="form-check-input"
                                                            type="radio"
                                                            name="maamDelivery"
                                                            id="maamDeliveryDownload"
                                                            value="download"
                                                            checked={maamDelivery === 'download'}
                                                            onChange={() => setMaamDelivery('download')}
                                                        />
                                                        <label className="form-check-label" htmlFor="maamDeliveryDownload">
                                                            הורד מיידית
                                                        </label>
                                                    </div>
                                                    <div className="form-check d-flex align-items-center">
                                                        <input
                                                            className="form-check-input"
                                                            type="radio"
                                                            name="maamDelivery"
                                                            id="maamDeliveryEmail"
                                                            value="email"
                                                            checked={maamDelivery === 'email'}
                                                            onChange={() => setMaamDelivery('email')}
                                                        />
                                                        <label className="form-check-label" htmlFor="maamDeliveryEmail">
                                                            שליחה למייל
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                            {maamDelivery === 'email' && (
                                                <div className="mb-3">
                                                    <label className="form-label">כתובת מייל:</label>
                                                    <input type="email" className="form-control" value={maamEmail} onChange={e => setMaamEmail(e.target.value)} required />
                                                </div>
                                            )}
                                            {maamError && <div className="alert alert-danger">{maamError}</div>}
                                            {maamSuccess && <div className="alert alert-success">{maamSuccess} {maamDownloadUrl && (<button type="button" className="btn btn-link p-0" onClick={() => handleDownload(maamDownloadUrl)}>הורד דו"ח</button>)}</div>}
                                            <button type="submit" className="btn btn-primary" disabled={maamLoading}>{maamLoading ? "מפיקים..." : "הפק דו" + String.fromCharCode(8221) + "ח"}</button>
                                        </form>
                                    </div>
                                </div>
                            </div>
                            {/* דו"ח הכנסות */}
                            <div className="accordion-item">
                                <h2 className="accordion-header" id="headingIncome">
                                    <button
                                        className={`accordion-button${openAccordion === "income" ? "" : " collapsed"}`}
                                        type="button"
                                        onClick={() => setOpenAccordion(openAccordion === "income" ? null : "income")}
                                        aria-expanded={openAccordion === "income"}
                                        aria-controls="collapseIncome"
                                    >
                                        דו"ח הכנסות
                                    </button>
                                </h2>
                                <div
                                    id="collapseIncome"
                                    className={`accordion-collapse collapse${openAccordion === "income" ? " show" : ""}`}
                                    aria-labelledby="headingIncome"
                                    data-bs-parent="#reportsAccordion"
                                >
                                    <div className="accordion-body">
                                        <form onSubmit={handleIncomeSubmit}>
                                            <span className="text-sm fw-medium">טווח תאריכים:</span>
                                            <div className="d-flex align-items-start align-items-sm-center gap-2 flex-wrap mt-2 mb-3 flex-column flex-sm-row">
                                                <div className="d-flex gap-1 gap-sm-2 flex-column flex-sm-row align-items-start align-items-sm-center">
                                                    <div className="position-relative">
                                                        <DatePicker
                                                            selected={incomeStartDate}
                                                            onChange={date => {
                                                                setIncomeStartDate(date);
                                                                if (date && incomeEndDate && date > incomeEndDate) {
                                                                    setIncomeEndDate(date);
                                                                }
                                                            }}
                                                            selectsStart
                                                            startDate={incomeStartDate}
                                                            endDate={incomeEndDate}
                                                            className="form-control form-control-sm"
                                                            dateFormat="dd/MM/yyyy"
                                                            placeholderText="תאריך התחלה"
                                                            locale="he"
                                                            minDate={new Date(2020, 0, 1)}
                                                            maxDate={incomeEndDate || new Date(2030, 11, 31)}
                                                        />
                                                    </div>
                                                    <span>עד</span>
                                                    <div className="position-relative">
                                                        <DatePicker
                                                            selected={incomeEndDate}
                                                            onChange={date => {
                                                                setIncomeEndDate(date);
                                                                if (date && incomeStartDate && date < incomeStartDate) {
                                                                    setIncomeStartDate(date);
                                                                }
                                                            }}
                                                            selectsEnd
                                                            startDate={incomeStartDate}
                                                            endDate={incomeEndDate}
                                                            className="form-control form-control-sm"
                                                            dateFormat="dd/MM/yyyy"
                                                            placeholderText="תאריך סיום"
                                                            locale="he"
                                                            minDate={incomeStartDate || new Date(2020, 0, 1)}
                                                            maxDate={new Date(2030, 11, 31)}
                                                        />

                                                    </div>
                                                </div>
                                                <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setQuickDateRange('prevBiMonth', setIncomeStartDate, setIncomeEndDate)}>איפוס</button>
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label">כתובת מייל:</label>
                                                <input type="email" className="form-control" value={incomeEmail} onChange={e => setIncomeEmail(e.target.value)} required />
                                            </div>
                                            {incomeError && <div className="alert alert-danger">{incomeError}</div>}
                                            {incomeSuccess && <div className="alert alert-success">{incomeSuccess}</div>}
                                            <button type="submit" className="btn btn-primary" disabled={incomeLoading}>{incomeLoading ? "מפיקים..." : "הפק דו" + String.fromCharCode(8221) + "ח"}</button>
                                        </form>
                                    </div>
                                </div>
                            </div>
                            {/* דו"ח הוצאות */}
                            <div className="accordion-item">
                                <h2 className="accordion-header" id="headingExpense">
                                    <button
                                        className={`accordion-button${openAccordion === "expense" ? "" : " collapsed"}`}
                                        type="button"
                                        onClick={() => setOpenAccordion(openAccordion === "expense" ? null : "expense")}
                                        aria-expanded={openAccordion === "expense"}
                                        aria-controls="collapseExpense"
                                    >
                                        דו"ח הוצאות
                                    </button>
                                </h2>
                                <div
                                    id="collapseExpense"
                                    className={`accordion-collapse collapse${openAccordion === "expense" ? " show" : ""}`}
                                    aria-labelledby="headingExpense"
                                    data-bs-parent="#reportsAccordion"
                                >
                                    <div className="accordion-body">
                                        <form onSubmit={handleExpenseSubmit}>
                                            <span className="text-sm fw-medium">טווח תאריכים:</span>
                                            <div className="d-flex align-items-start align-items-sm-center gap-2 flex-wrap mt-2 mb-3 flex-column flex-sm-row">
                                                <div className="d-flex gap-1 gap-sm-2 flex-column flex-sm-row align-items-start align-items-sm-center">
                                                    <div className="position-relative">
                                                        <DatePicker
                                                            selected={expenseStartDate}
                                                            onChange={date => {
                                                                setExpenseStartDate(date);
                                                                if (date && expenseEndDate && date > expenseEndDate) {
                                                                    setExpenseEndDate(date);
                                                                }
                                                            }}
                                                            selectsStart
                                                            startDate={expenseStartDate}
                                                            endDate={expenseEndDate}
                                                            className="form-control form-control-sm"
                                                            dateFormat="dd/MM/yyyy"
                                                            placeholderText="תאריך התחלה"
                                                            locale="he"
                                                            minDate={new Date(2020, 0, 1)}
                                                            maxDate={expenseEndDate || new Date(2030, 11, 31)}
                                                        />
                                                    </div>
                                                    <span>עד</span>
                                                    <div className="position-relative">
                                                        <DatePicker
                                                            selected={expenseEndDate}
                                                            onChange={date => {
                                                                setExpenseEndDate(date);
                                                                if (date && expenseStartDate && date < expenseStartDate) {
                                                                    setExpenseStartDate(date);
                                                                }
                                                            }}
                                                            selectsEnd
                                                            startDate={expenseStartDate}
                                                            endDate={expenseEndDate}
                                                            className="form-control form-control-sm"
                                                            dateFormat="dd/MM/yyyy"
                                                            placeholderText="תאריך סיום"
                                                            locale="he"
                                                            minDate={expenseStartDate || new Date(2020, 0, 1)}
                                                            maxDate={new Date(2030, 11, 31)}
                                                        />

                                                    </div>
                                                </div>
                                                <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setQuickDateRange('prevBiMonth', setExpenseStartDate, setExpenseEndDate)}>איפוס</button>
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label">כתובת מייל:</label>
                                                <input type="email" className="form-control" value={expenseEmail} onChange={e => setExpenseEmail(e.target.value)} required />
                                            </div>
                                            {expenseError && <div className="alert alert-danger">{expenseError}</div>}
                                            {expenseSuccess && <div className="alert alert-success">{expenseSuccess}</div>}
                                            <button type="submit" className="btn btn-primary" disabled={expenseLoading}>{expenseLoading ? "מפיקים..." : "הפק דו" + String.fromCharCode(8221) + "ח"}</button>
                                        </form>
                                    </div>
                                </div>
                            </div>
                            {/* דו"ח עסקאות */}
                            <div className="accordion-item">
                                <h2 className="accordion-header" id="headingTransactions">
                                    <button
                                        className={`accordion-button${openAccordion === "transactions" ? "" : " collapsed"}`}
                                        type="button"
                                        onClick={() => setOpenAccordion(openAccordion === "transactions" ? null : "transactions")}
                                        aria-expanded={openAccordion === "transactions"}
                                        aria-controls="collapseTransactions"
                                    >
                                        דו"ח עסקאות
                                    </button>
                                </h2>
                                <div
                                    id="collapseTransactions"
                                    className={`accordion-collapse collapse${openAccordion === "transactions" ? " show" : ""}`}
                                    aria-labelledby="headingTransactions"
                                    data-bs-parent="#reportsAccordion"
                                >
                                    <div className="accordion-body">
                                        <form onSubmit={handleTransactionsSubmit}>
                                            <span className="text-sm fw-medium">טווח תאריכים:</span>
                                            <div className="d-flex align-items-start align-items-sm-center gap-2 flex-wrap mt-2 mb-3 flex-column flex-sm-row">
                                                <div className="d-flex gap-1 gap-sm-2 flex-column flex-sm-row align-items-start align-items-sm-center">
                                                    <div className="position-relative">
                                                        <DatePicker
                                                            selected={transactionsStartDate}
                                                            onChange={date => {
                                                                setTransactionsStartDate(date);
                                                                if (date && transactionsEndDate && date > transactionsEndDate) {
                                                                    setTransactionsEndDate(date);
                                                                }
                                                            }}
                                                            selectsStart
                                                            startDate={transactionsStartDate}
                                                            endDate={transactionsEndDate}
                                                            className="form-control form-control-sm"
                                                            dateFormat="dd/MM/yyyy"
                                                            placeholderText="תאריך התחלה"
                                                            locale="he"
                                                            minDate={new Date(2020, 0, 1)}
                                                            maxDate={transactionsEndDate || new Date(2030, 11, 31)}
                                                        />
                                                    </div>
                                                    <span>עד</span>
                                                    <div className="position-relative">
                                                        <DatePicker
                                                            selected={transactionsEndDate}
                                                            onChange={date => {
                                                                setTransactionsEndDate(date);
                                                                if (date && transactionsStartDate && date < transactionsStartDate) {
                                                                    setTransactionsStartDate(date);
                                                                }
                                                            }}
                                                            selectsEnd
                                                            startDate={transactionsStartDate}
                                                            endDate={transactionsEndDate}
                                                            className="form-control form-control-sm"
                                                            dateFormat="dd/MM/yyyy"
                                                            placeholderText="תאריך סיום"
                                                            locale="he"
                                                            minDate={transactionsStartDate || new Date(2020, 0, 1)}
                                                            maxDate={new Date(2030, 11, 31)}
                                                        />
                                                    </div>
                                                </div>
                                                <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setQuickDateRange('prevBiMonth', setTransactionsStartDate, setTransactionsEndDate)}>איפוס</button>
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label">אופן קבלת הדו"ח:</label>
                                                <div className="d-flex gap-0 gap-sm-5 align-items-start align-items-sm-center flex-column flex-sm-row">
                                                    <div className="form-check d-flex align-items-center">
                                                        <input
                                                            className="form-check-input"
                                                            type="radio"
                                                            name="transactionsDelivery"
                                                            id="transactionsDeliveryDownload"
                                                            value="download"
                                                            checked={transactionsDelivery === 'download'}
                                                            onChange={() => setTransactionsDelivery('download')}
                                                        />
                                                        <label className="form-check-label" htmlFor="transactionsDeliveryDownload">
                                                            הורד מיידית
                                                        </label>
                                                    </div>
                                                    <div className="form-check d-flex align-items-center">
                                                        <input
                                                            className="form-check-input"
                                                            type="radio"
                                                            name="transactionsDelivery"
                                                            id="transactionsDeliveryEmail"
                                                            value="email"
                                                            checked={transactionsDelivery === 'email'}
                                                            onChange={() => setTransactionsDelivery('email')}
                                                        />
                                                        <label className="form-check-label" htmlFor="transactionsDeliveryEmail">
                                                            שליחה למייל
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                            {transactionsDelivery === 'email' && (
                                                <div className="mb-3">
                                                    <label className="form-label">כתובת מייל:</label>
                                                    <input type="email" className="form-control" value={transactionsEmail} onChange={e => setTransactionsEmail(e.target.value)} required />
                                                </div>
                                            )}
                                            <div className="mb-3 d-flex align-items-center gap-2">
                                                <label className="form-label m-0">תלמיד (לא חובה):</label>
                                                <button type="button" className="btn btn-outline-primary" onClick={handleOpenStudentModal}>
                                                    {transactionsStudentUID
                                                        ? (selectedStudent ? [selectedStudent.firstName, selectedStudent.lastName].filter(Boolean).join(' ') : 'תלמיד נבחר')
                                                        : 'בחר תלמיד'}
                                                </button>
                                                {transactionsStudentUID && (
                                                    <button type="button" className="btn btn-link text-danger" onClick={() => setTransactionsStudentUID("")}>איפוס</button>
                                                )}
                                            </div>
                                            <div className="form-check mb-3 d-flex align-items-center">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id="ignoreCredit"
                                                    checked={transactionsIgnoreCredit}
                                                    onChange={e => setTransactionsIgnoreCredit(e.target.checked)}
                                                />
                                                <label className="form-check-label" htmlFor="ignoreCredit">
                                                    התעלם מהעברות יתרה
                                                </label>
                                            </div>
                                            {transactionsError && <div className="alert alert-danger">{transactionsError}</div>}
                                            {transactionsSuccess && <div className="alert alert-success">{transactionsSuccess} {transactionsDownloadUrl && (<button type="button" className="btn btn-link p-0" onClick={() => handleDownload(transactionsDownloadUrl)}>הורד דו"ח</button>)}</div>}
                                            <button type="submit" className="btn btn-primary" disabled={transactionsLoading}>{transactionsLoading ? "מפיקים..." : "הפק דו" + String.fromCharCode(8221) + "ח"}</button>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* Group: לימוד */}
                        <div className="mb-4">
                            <h4 className="mt-20 mb-3 text-xl opacity-75">לימוד</h4>
                            {/* דו"ח אירועים */}
                            <div className="accordion-item">
                                <h2 className="accordion-header" id="headingEvents">
                                    <button
                                        className={`accordion-button${openAccordion === "events" ? "" : " collapsed"}`}
                                        type="button"
                                        onClick={() => setOpenAccordion(openAccordion === "events" ? null : "events")}
                                        aria-expanded={openAccordion === "events"}
                                        aria-controls="collapseEvents"
                                    >
                                        דו"ח יומן
                                    </button>
                                </h2>
                                <div
                                    id="collapseEvents"
                                    className={`accordion-collapse collapse${openAccordion === "events" ? " show" : ""}`}
                                    aria-labelledby="headingEvents"
                                    data-bs-parent="#reportsAccordion"
                                >
                                    <div className="accordion-body">
                                        <form onSubmit={handleEventsSubmit} className="row g-2">
                                            <span className="text-sm fw-medium col-12">טווח תאריכים:</span>
                                            <div className="d-flex gap-4 gap-sm-2 flex-wrap mt-2 mb-3 col-12 col-md-auto align-items-start align-items-sm-center flex-column flex-sm-row">
                                                <div className="d-flex gap-0 gap-sm-2 align-items-start align-items-sm-center flex-column flex-sm-row">
                                                    <div className="position-relative">
                                                        <DatePicker
                                                            selected={eventsStartDate}
                                                            onChange={date => {
                                                                setEventsStartDate(date);
                                                                if (date && eventsEndDate && date > eventsEndDate) {
                                                                    setEventsEndDate(date);
                                                                }
                                                            }}
                                                            selectsStart
                                                            startDate={eventsStartDate}
                                                            endDate={eventsEndDate}
                                                            className="form-control form-control-sm"
                                                            dateFormat="dd/MM/yyyy"
                                                            placeholderText="תאריך התחלה"
                                                            locale="he"
                                                            minDate={new Date(2020, 0, 1)}
                                                            maxDate={eventsEndDate || new Date(2030, 11, 31)}
                                                        />
                                                    </div>
                                                    <span>עד</span>
                                                    <div className="position-relative">
                                                        <DatePicker
                                                            selected={eventsEndDate}
                                                            onChange={date => {
                                                                setEventsEndDate(date);
                                                                if (date && eventsStartDate && date < eventsStartDate) {
                                                                    setEventsStartDate(date);
                                                                }
                                                            }}
                                                            selectsEnd
                                                            startDate={eventsStartDate}
                                                            endDate={eventsEndDate}
                                                            className="form-control form-control-sm"
                                                            dateFormat="dd/MM/yyyy"
                                                            placeholderText="תאריך סיום"
                                                            locale="he"
                                                            minDate={eventsStartDate || new Date(2020, 0, 1)}
                                                            maxDate={new Date(2030, 11, 31)}
                                                        />
                                                    </div>
                                                </div>
                                                <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setQuickDateRange('prevBiMonth', setEventsStartDate, setEventsEndDate)}>איפוס</button>
                                            </div>
                                            <div className="mb-3 col-12 col-md-12">
                                                <label className="form-label">אופן קבלת הדו"ח:</label>
                                                <div className="d-flex gap-0 gap-sm-5 align-items-start align-items-sm-center flex-column flex-sm-row">
                                                    <div className="form-check d-flex align-items-center">
                                                        <input
                                                            className="form-check-input"
                                                            type="radio"
                                                            name="eventsDelivery"
                                                            id="eventsDeliveryDownload"
                                                            value="download"
                                                            checked={eventsDelivery === 'download'}
                                                            onChange={() => setEventsDelivery('download')}
                                                        />
                                                        <label className="form-check-label" htmlFor="eventsDeliveryDownload">
                                                            הורד מיידית
                                                        </label>
                                                    </div>
                                                    <div className="form-check d-flex align-items-center">
                                                        <input
                                                            className="form-check-input"
                                                            type="radio"
                                                            name="eventsDelivery"
                                                            id="eventsDeliveryEmail"
                                                            value="email"
                                                            checked={eventsDelivery === 'email'}
                                                            onChange={() => setEventsDelivery('email')}
                                                        />
                                                        <label className="form-check-label" htmlFor="eventsDeliveryEmail">
                                                            שליחה למייל
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                            {eventsDelivery === 'email' && (
                                                <div className="mb-3 col-12 col-md-6">
                                                    <label className="form-label">כתובת מייל:</label>
                                                    <input type="email" className="form-control" value={eventsEmail} onChange={e => setEventsEmail(e.target.value)} required />
                                                </div>
                                            )}
                                            <div className="mb-3 d-flex align-items-center gap-2 flex-wrap col-12 col-md-8">
                                                <label className="form-label m-0">תלמיד (לא חובה):</label>
                                                <button type="button" className="btn btn-outline-primary" onClick={handleOpenEventsStudentModal}>
                                                    {eventsStudentUID
                                                        ? (selectedEventsStudent ? [selectedEventsStudent.firstName, selectedEventsStudent.lastName].filter(Boolean).join(' ') : 'תלמיד נבחר')
                                                        : 'בחר תלמיד'}
                                                </button>
                                                {eventsStudentUID && (
                                                    <button type="button" className="btn btn-link text-danger" onClick={() => setEventsStudentUID("")}>איפוס</button>
                                                )}
                                            </div>
                                            <div className="mb-3 col-12">
                                                <label className="form-label">סוגי אירועים:</label>
                                                <div className="d-flex flex-wrap gap-2 flex-column flex-sm-row">
                                                    {EVENT_TYPES.map(type => (
                                                        <div className="form-check form-check-inline d-flex align-items-center" key={type.value}>
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                id={`eventType_${type.value}`}
                                                                value={type.value}
                                                                checked={eventsTypes.includes(type.value)}
                                                                onChange={e => {
                                                                    if (e.target.checked) {
                                                                        setEventsTypes([...eventsTypes, type.value]);
                                                                    } else {
                                                                        setEventsTypes(eventsTypes.filter(t => t !== type.value));
                                                                    }
                                                                }}
                                                            />
                                                            <label className="form-check-label" htmlFor={`eventType_${type.value}`}>{type.label}</label>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                            {eventsError && <div className="alert alert-danger col-12">{eventsError}</div>}
                                            {eventsSuccess && <div className="alert alert-success col-12">{eventsSuccess} {eventsDownloadUrl && (<button type="button" className="btn btn-link p-0" onClick={() => handleDownload(eventsDownloadUrl)}>הורד דו"ח</button>)}</div>}
                                            <button type="submit" className="btn btn-primary w-100 w-md-auto col-12 col-md-auto" disabled={eventsLoading}>{eventsLoading ? "מפיקים..." : "הפק דו" + String.fromCharCode(8221) + "ח"}</button>
                                        </form>
                                    </div>
                                </div>
                            </div>
                            {/* דו"ח תלמידים */}
                            <div className="accordion-item">
                                <h2 className="accordion-header" id="headingStudents">
                                    <button
                                        className={`accordion-button${openAccordion === "students" ? "" : " collapsed"}`}
                                        type="button"
                                        onClick={() => setOpenAccordion(openAccordion === "students" ? null : "students")}
                                        aria-expanded={openAccordion === "students"}
                                        aria-controls="collapseStudents"
                                    >
                                        דו"ח תלמידים
                                    </button>
                                </h2>
                                <div
                                    id="collapseStudents"
                                    className={`accordion-collapse collapse${openAccordion === "students" ? " show" : ""}`}
                                    aria-labelledby="headingStudents"
                                    data-bs-parent="#reportsAccordion"
                                >
                                    <div className="accordion-body">
                                        <form onSubmit={handleStudentsSubmit}>
                                            <div className="mb-3">
                                                <label className="form-label">אופן קבלת הדו"ח:</label>
                                                <div className="d-flex gap-0 gap-sm-5 align-items-start align-items-sm-center flex-column flex-sm-row">
                                                    <div className="form-check d-flex align-items-center">
                                                        <input
                                                            className="form-check-input"
                                                            type="radio"
                                                            name="studentsDelivery"
                                                            id="studentsDeliveryDownload"
                                                            value="download"
                                                            checked={studentsDelivery === 'download'}
                                                            onChange={() => setStudentsDelivery('download')}
                                                        />
                                                        <label className="form-check-label" htmlFor="studentsDeliveryDownload">
                                                            הורד מיידית
                                                        </label>
                                                    </div>
                                                    <div className="form-check d-flex align-items-center">
                                                        <input
                                                            className="form-check-input"
                                                            type="radio"
                                                            name="studentsDelivery"
                                                            id="studentsDeliveryEmail"
                                                            value="email"
                                                            checked={studentsDelivery === 'email'}
                                                            onChange={() => setStudentsDelivery('email')}
                                                        />
                                                        <label className="form-check-label" htmlFor="studentsDeliveryEmail">
                                                            שליחה למייל
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                            {studentsDelivery === 'email' && (
                                                <div className="mb-3">
                                                    <label className="form-label">כתובת מייל:</label>
                                                    <input type="email" className="form-control" value={studentsEmail} onChange={e => setStudentsEmail(e.target.value)} required />
                                                </div>
                                            )}
                                            {studentsError && <div className="alert alert-danger">{studentsError}</div>}
                                            {studentsSuccess && <div className="alert alert-success">{studentsSuccess} {studentsDownloadUrl && (<button type="button" className="btn btn-link p-0" onClick={() => handleDownload(studentsDownloadUrl)}>הורד דו"ח</button>)}</div>}
                                            <button type="submit" className="btn btn-primary" disabled={studentsLoading}>{studentsLoading ? "מפיקים..." : "הפק דו" + String.fromCharCode(8221) + "ח"}</button>
                                        </form>
                                    </div>
                                </div>
                            </div>
                            {/* Student picker modal for events report */}
                            {showEventsStudentModal && (
                                <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.3)' }}>
                                    <div className="modal-dialog modal-dialog-scrollable">
                                        <div className="modal-content">
                                            <div className="modal-header">
                                                <h5 className="modal-title">בחר תלמיד</h5>
                                                <button type="button" className="btn-close" onClick={() => setShowEventsStudentModal(false)}></button>
                                            </div>
                                            <div className="modal-body">
                                                <div className="mb-3 d-flex gap-3 align-items-center">
                                                    <span>סטטוס:</span>
                                                    <div className="form-check form-check-inline d-flex align-items-center">
                                                        <input
                                                            className="form-check-input"
                                                            type="radio"
                                                            name="eventsStudentStatus"
                                                            id="eventsStudentStatusActive"
                                                            value="active"
                                                            checked={eventsStudentStatus === 'active'}
                                                            onChange={() => setEventsStudentStatus('active')}
                                                        />
                                                        <label className="form-check-label" htmlFor="eventsStudentStatusActive">פעילים</label>
                                                    </div>
                                                    <div className="form-check form-check-inline d-flex align-items-center">
                                                        <input
                                                            className="form-check-input"
                                                            type="radio"
                                                            name="eventsStudentStatus"
                                                            id="eventsStudentStatusInactive"
                                                            value="inactive"
                                                            checked={eventsStudentStatus === 'inactive'}
                                                            onChange={() => setEventsStudentStatus('inactive')}
                                                        />
                                                        <label className="form-check-label" htmlFor="eventsStudentStatusInactive">לא פעילים</label>
                                                    </div>
                                                </div>
                                                <input
                                                    type="text"
                                                    className="form-control mb-3"
                                                    placeholder="חפש תלמיד..."
                                                    value={eventsStudentSearch}
                                                    onChange={e => setEventsStudentSearch(e.target.value)}
                                                    autoFocus
                                                />
                                                <div style={{ maxHeight: 350, overflowY: 'auto' }}>
                                                    {users.filter(s =>
                                                        (eventsStudentStatus === 'active' ? s.status !== 'לא פעיל' && s.status !== 'inactive' : s.status === 'לא פעיל' || s.status === 'inactive') &&
                                                        (s.name || `${s.firstName} ${s.lastName}`)
                                                            .toLowerCase()
                                                            .includes(eventsStudentSearch.toLowerCase())
                                                    ).map(student => (
                                                        <div key={student.uid || student.id} className="d-flex align-items-center justify-content-between border-bottom py-2">
                                                            <span>{student.name || `${student.firstName} ${student.lastName}`}</span>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-primary"
                                                                onClick={() => {
                                                                    setEventsStudentUID(student.uid || student.id);
                                                                    setShowEventsStudentModal(false);
                                                                    setEventsStudentSearch("");
                                                                }}
                                                            >בחר</button>
                                                        </div>
                                                    ))}
                                                    {users.length === 0 && <div className="text-center text-muted">לא נמצאו תלמידים</div>}
                                                </div>
                                            </div>
                                            <div className="modal-footer">
                                                <button type="button" className="btn btn-secondary" onClick={() => setShowEventsStudentModal(false)}>סגור</button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        {/* More groups can be added here */}
                    </div>
                </div>
            </MasterLayout>
            {showStudentModal && (
                <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.3)' }}>
                    <div className="modal-dialog modal-dialog-scrollable">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">בחר תלמיד</h5>
                                <button type="button" className="btn-close" onClick={() => setShowStudentModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3 d-flex gap-3 align-items-center">
                                    <span>סטטוס:</span>
                                    <div className="form-check form-check-inline d-flex align-items-center">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="studentStatus"
                                            id="studentStatusActive"
                                            value="active"
                                            checked={studentStatus === 'active'}
                                            onChange={() => setStudentStatus('active')}
                                        />
                                        <label className="form-check-label" htmlFor="studentStatusActive">פעילים</label>
                                    </div>
                                    <div className="form-check form-check-inline d-flex align-items-center">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="studentStatus"
                                            id="studentStatusInactive"
                                            value="inactive"
                                            checked={studentStatus === 'inactive'}
                                            onChange={() => setStudentStatus('inactive')}
                                        />
                                        <label className="form-check-label" htmlFor="studentStatusInactive">לא פעילים</label>
                                    </div>
                                </div>
                                <input
                                    type="text"
                                    className="form-control mb-3"
                                    placeholder="חפש תלמיד..."
                                    value={studentSearch}
                                    onChange={e => setStudentSearch(e.target.value)}
                                    autoFocus
                                />
                                <div style={{ maxHeight: 350, overflowY: 'auto' }}>
                                    {users.filter(s =>
                                        (studentStatus === 'active' ? s.status !== 'לא פעיל' && s.status !== 'inactive' : s.status === 'לא פעיל' || s.status === 'inactive') &&
                                        (s.name || `${s.firstName} ${s.lastName}`)
                                            .toLowerCase()
                                            .includes(studentSearch.toLowerCase())
                                    ).map(student => (
                                        <div key={student.uid || student.id} className="d-flex align-items-center justify-content-between border-bottom py-2">
                                            <span>{student.name || `${student.firstName} ${student.lastName}`}</span>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-primary"
                                                onClick={() => {
                                                    setTransactionsStudentUID(student.uid || student.id);
                                                    setShowStudentModal(false);
                                                    setStudentSearch("");
                                                }}
                                            >בחר</button>
                                        </div>
                                    ))}
                                    {users.length === 0 && <div className="text-center text-muted">לא נמצאו תלמידים</div>}
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowStudentModal(false)}>סגור</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ReportsPage; 